<template>
  <div class="flows-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="custom-flows-view">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-title-section">
          <h1 class="page-title">WhatsApp Custom Flows</h1>
          <p class="page-subtitle">Create and manage custom conversation flows with API integrations</p>
        </div>
        <button @click="createNewFlow" class="btn-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="12" y1="5" x2="12" y2="19" stroke-width="2" stroke-linecap="round"/>
            <line x1="5" y1="12" x2="19" y2="12" stroke-width="2" stroke-linecap="round"/>
          </svg>
          Create New Flow
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1); color: #3b82f6;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z M21,16V14H14V16H21M21,11V9H14V11H21Z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Total Flows</div>
          <div class="stat-value">{{ totalFlows }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(16, 185, 129, 0.1); color: #10b981;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Active Flows</div>
          <div class="stat-value">{{ activeFlows }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(245, 158, 11, 0.1); color: #f59e0b;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Total Sessions</div>
          <div class="stat-value">{{ totalSessions }}</div>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon" style="background: rgba(139, 92, 246, 0.1); color: #8b5cf6;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 13v8h8v-8h-8zM3 21h8v-8H3v8zM3 3v8h8V3H3zm13.66-1.31L11 7.34 16.66 13l5.66-5.66-5.66-5.65z"/>
          </svg>
        </div>
        <div class="stat-content">
          <div class="stat-label">Completions</div>
          <div class="stat-value">{{ totalCompletions }}</div>
        </div>
      </div>
    </div>

    <!-- Filters and Search -->
    <div class="filters-bar">
      <div class="search-box">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="11" cy="11" r="8" stroke-width="2"/>
          <path d="M21 21l-4.35-4.35" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search flows..."
          class="search-input"
        />
      </div>

      <div class="filter-buttons">
        <button
          @click="filterStatus = 'all'"
          :class="['filter-btn', { active: filterStatus === 'all' }]"
        >
          All
        </button>
        <button
          @click="filterStatus = 'active'"
          :class="['filter-btn', { active: filterStatus === 'active' }]"
        >
          Active
        </button>
        <button
          @click="filterStatus = 'inactive'"
          :class="['filter-btn', { active: filterStatus === 'inactive' }]"
        >
          Inactive
        </button>
      </div>
    </div>

    <!-- Flows Grid -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading flows...</p>
    </div>

    <div v-else-if="filteredFlows.length === 0" class="empty-state">
      <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" opacity="0.3">
        <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z M21,16V14H14V16H21M21,11V9H14V11H21Z" stroke-width="2"/>
      </svg>
      <h3>No flows found</h3>
      <p>Create your first custom WhatsApp flow to get started</p>
      <button @click="createNewFlow" class="btn-primary">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <line x1="12" y1="5" x2="12" y2="19" stroke-width="2" stroke-linecap="round"/>
          <line x1="5" y1="12" x2="19" y2="12" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Create New Flow
      </button>
    </div>

    <div v-else class="flows-grid">
      <div
        v-for="flow in filteredFlows"
        :key="flow.id"
        class="flow-card"
        :class="{ inactive: !flow.enabled }"
      >
        <div class="flow-card-header">
          <div class="flow-status-badge" :class="{ active: flow.enabled, inactive: !flow.enabled }">
            {{ flow.enabled ? 'Active' : 'Inactive' }}
          </div>
          <div class="flow-actions">
            <button @click="editFlow(flow.id)" class="icon-btn" title="Edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button @click="toggleFlowStatus(flow)" class="icon-btn" :title="flow.enabled ? 'Deactivate' : 'Activate'">
              <svg v-if="flow.enabled" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="9 11 12 14 22 4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button @click="deleteFlow(flow)" class="icon-btn danger" title="Delete">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="3 6 5 6 21 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="flow-card-content" @click="editFlow(flow.id)">
          <h3 class="flow-name">{{ flow.flow_name }}</h3>
          <p class="flow-description">{{ flow.description || 'No description' }}</p>

          <div class="flow-stats">
            <div class="flow-stat-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2z"/>
              </svg>
              <span>{{ flow.total_sessions || 0 }} sessions</span>
            </div>
            <div class="flow-stat-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>{{ flow.total_submissions || 0 }} completed</span>
            </div>
          </div>

          <div class="flow-meta">
            <span class="meta-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke-width="2"/>
                <line x1="16" y1="2" x2="16" y2="6" stroke-width="2" stroke-linecap="round"/>
                <line x1="8" y1="2" x2="8" y2="6" stroke-width="2" stroke-linecap="round"/>
                <line x1="3" y1="10" x2="21" y2="10" stroke-width="2"/>
              </svg>
              {{ formatDate(flow.created_at) }}
            </span>
          </div>
        </div>
      </div>
    </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import DashboardService from '../services/dashboard'
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'

export default {
  name: 'CustomFlowsView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      flows: [],
      loading: true,
      searchQuery: '',
      filterStatus: 'all',
      totalFlows: 0,
      activeFlows: 0,
      totalSessions: 0,
      totalCompletions: 0
    }
  },

  computed: {
    filteredFlows () {
      let filtered = this.flows

      // Filter by status
      if (this.filterStatus === 'active') {
        filtered = filtered.filter(flow => flow.enabled)
      } else if (this.filterStatus === 'inactive') {
        filtered = filtered.filter(flow => !flow.enabled)
      }

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(flow =>
          flow.flow_name.toLowerCase().includes(query) ||
          (flow.description && flow.description.toLowerCase().includes(query))
        )
      }

      return filtered
    }
  },

  mounted () {
    this.loadFlows()
  },

  methods: {
    async loadFlows () {
      this.loading = true
      try {
        const response = await DashboardService.getCustomFlows()
        console.log('Custom flows response:', response)

        if (response && response.success && response.flows) {
          this.flows = response.flows
          this.calculateStats()
        } else if (response && response.success && Array.isArray(response.data)) {
          // Handle if the API returns flows in a 'data' property
          this.flows = response.data
          this.calculateStats()
        } else {
          // No flows yet, show empty state
          this.flows = []
          this.calculateStats()
        }
      } catch (error) {
        console.error('Failed to load flows:', error)
        // Show error message using alert if toast is not available
        if (this.$toast) {
          this.$toast.error('Failed to load flows')
        } else {
          console.error('Failed to load flows - toast not available')
        }
        this.flows = []
      } finally {
        this.loading = false
      }
    },

    calculateStats () {
      this.totalFlows = this.flows.length
      this.activeFlows = this.flows.filter(f => f.enabled).length
      this.totalSessions = this.flows.reduce((sum, f) => sum + (f.total_sessions || 0), 0)
      this.totalCompletions = this.flows.reduce((sum, f) => sum + (f.total_submissions || 0), 0)
    },

    createNewFlow () {
      this.$router.push('/flow-builder')
    },

    editFlow (flowId) {
      this.$router.push(`/flow-builder/${flowId}`)
    },

    async toggleFlowStatus (flow) {
      try {
        const response = await DashboardService.updateCustomFlow(flow.id, {
          ...flow,
          enabled: !flow.enabled
        })

        if (response && response.success) {
          flow.enabled = !flow.enabled
          this.calculateStats()
          if (this.$toast) {
            this.$toast.success(flow.enabled ? 'Flow activated' : 'Flow deactivated')
          }
        }
      } catch (error) {
        console.error('Failed to toggle flow status:', error)
        if (this.$toast) {
          this.$toast.error('Failed to update flow status')
        }
      }
    },

    async deleteFlow (flow) {
      if (!confirm(`Are you sure you want to delete "${flow.flow_name}"? This action cannot be undone.`)) {
        return
      }

      try {
        const response = await DashboardService.deleteCustomFlow(flow.id)
        if (response && response.success) {
          this.flows = this.flows.filter(f => f.id !== flow.id)
          this.calculateStats()
          if (this.$toast) {
            this.$toast.success('Flow deleted successfully')
          }
        }
      } catch (error) {
        console.error('Failed to delete flow:', error)
        if (this.$toast) {
          this.$toast.error('Failed to delete flow')
        }
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
/* Layout Structure */
.flows-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.custom-flows-view {
  background: var(--bg-secondary);
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.header-title-section {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-bottom: 4px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

/* Filters Bar */
.filters-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
  display: flex;
  align-items: center;
}

.search-box svg {
  position: absolute;
  left: 16px;
  color: var(--text-tertiary);
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-buttons {
  display: flex;
  gap: 8px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  padding: 4px;
  border-radius: 10px;
}

.filter-btn {
  padding: 8px 16px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.filter-btn.active {
  background: var(--primary-color);
  color: white;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-tertiary);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  color: var(--text-tertiary);
}

.empty-state h3 {
  margin: 16px 0 8px;
  font-size: 20px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0 0 24px;
  font-size: 14px;
}

/* Flows Grid */
.flows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.flow-card {
  background: var(--card-background);
  border: 2px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
}

.flow-card:hover {
  border-color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
}

.flow-card.inactive {
  opacity: 0.6;
}

.flow-card-header {
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--hover-background);
}

.flow-status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.flow-status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.flow-status-badge.inactive {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.flow-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.icon-btn:hover {
  background: var(--hover-background);
  color: var(--text-primary);
  border-color: var(--text-primary);
}

.icon-btn.danger:hover {
  background: #fee;
  color: #ef4444;
  border-color: #ef4444;
}

.flow-card-content {
  padding: 20px;
}

.flow-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.flow-description {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.flow-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.flow-stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.flow-stat-item svg {
  color: var(--primary-color);
}

.flow-meta {
  display: flex;
  gap: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.meta-item svg {
  color: var(--text-tertiary);
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px;
  }

  .page-content {
    padding: 16px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-bar {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .filter-buttons {
    width: 100%;
    justify-content: space-between;
  }

  .flows-grid {
    grid-template-columns: 1fr;
  }
}
</style>
